import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import { ReactElement, Ref, forwardRef } from 'react';

import { TransitionDefaults, Variants, withDelay } from './utils';

const variants: Variants = {
  enter: ({ transition, transitionEnd, delay } = {}) => ({
    opacity: 1,
    y: 1,
    visibility: 'visible',
    transition: transition?.enter ?? withDelay.enter(TransitionDefaults.enter, delay),
    transitionEnd: transitionEnd?.enter,
  }),
  exit: ({ transition, transitionEnd, delay } = {}) => ({
    opacity: 0,
    y: 1,
    transition: transition?.exit ?? withDelay.exit(TransitionDefaults.exit, delay),
    transitionEnd: {
      ...transitionEnd?.exit,
      visibility: 'hidden',
    },
  }),
  initial: () => ({
    opacity: 0,
    y: -8,
  }),
};

export const dropdownFadeConfig: HTMLMotionProps<'div'> = {
  initial: 'initial',
  animate: 'enter',
  exit: 'exit',
  variants,
};

export type DropdownFadeProps = HTMLMotionProps<'div'> & {
  children?:
    | ReactElement
    | ((motionProps: HTMLMotionProps<'div'>, ref: Ref<HTMLDivElement>) => ReactElement);
  in?: boolean;
};

export type DropdownFadePresenceProps = DropdownFadeProps & {
  unmountOnExit?: boolean;
};

export const DropdownFade = forwardRef<HTMLDivElement, DropdownFadeProps>(function DropdownFade(
  props,
  ref
) {
  const { in: isOpen, children, transition, ...rest } = props;

  const custom = {
    transition,
  };

  const animate = isOpen ? 'enter' : 'exit';

  const motionProps = {
    custom,
    ...dropdownFadeConfig,
    animate,
    ...rest,
  };

  return typeof children === 'function' ? (
    children(motionProps, ref)
  ) : (
    <motion.div {...motionProps}>{children}</motion.div>
  );
});

export const DropdownFadePresence = forwardRef<HTMLDivElement, DropdownFadePresenceProps>(
  function DropdownFade(props, ref) {
    const { in: isOpen, unmountOnExit, transition, ...rest } = props;

    const custom = {
      transition,
    };

    const show = unmountOnExit ? isOpen && unmountOnExit : true;

    return (
      <AnimatePresence custom={custom}>
        {show && <DropdownFade in={isOpen} custom={custom} {...rest} />}
      </AnimatePresence>
    );
  }
);
